import { startOfDay as startOfDayInTz } from "@clipboard-health/date-time";
import { isDefined } from "@clipboard-health/util-ts";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { useFetchWorkplaceWorkerTypes } from "@src/appV2/FacilityDrawer/api/useFetchWorkplaceWorkerTypes";
import { useToast } from "@src/appV2/lib";
import { createFacilityShiftCountsQueryOptions } from "@src/appV2/redesign/DailyView/api/useGetFacilityShiftCounts";
import { useUpdateWorkerTypes } from "@src/appV2/redesign/DailyView/api/useUpdateWorkerTypes";
import { getQualificationsAvailableToDisplay } from "@src/appV2/Shifts/utils/getQualificationsAvailableToDisplay";
import { useQueries } from "@tanstack/react-query";
import { addDays } from "date-fns";
import { useEffect, useMemo, useState } from "react";

import { type SettingsPermissions } from "../../types";
import { CalendarDisplaySectionView } from "./CalendarDisplaySectionView";
import { useTu } from "@src/appV2/i18n/useTu";

interface CalendarDisplaySectionContainerProps {
  guardSettingsAction: (action: () => void) => void;
  permissions: SettingsPermissions;
  timezone: string;
  workplaceId: string;
}

interface WorkerTypeItem {
  hasActiveShifts: boolean;
  isWithoutCharge: boolean;
  name: string;
  visible: boolean;
}

export function CalendarDisplaySectionContainer({
  guardSettingsAction,
  permissions,
  timezone,
  workplaceId,
}: CalendarDisplaySectionContainerProps) {
  const { tu } = useTu("settings");
  const session = useSession();
  const { showSuccessToast, showErrorToast } = useToast();

  const { data: workerTypesResponse, isLoading: isWorkerTypesLoading } =
    useFetchWorkplaceWorkerTypes(
      { workplaceId, showHidden: true },
      { enabled: Boolean(workplaceId) }
    );

  const { isLoading: isUpdating, mutateAsync: updateWorkerTypes } = useUpdateWorkerTypes();

  const availableQualifications = useMemo(
    () => getQualificationsAvailableToDisplay(workerTypesResponse?.data?.workerTypes ?? []),
    [workerTypesResponse]
  );

  const initialWorkerTypes = useMemo(() => {
    const types = workerTypesResponse?.data?.workerTypes ?? [];
    // Force qualifications without a charge rate into the Hidden section.
    const adjusted = types.map((wt) => ({
      ...wt,
      visible: availableQualifications.has(wt.name) ? wt.visible : false,
    }));
    return adjusted.toSorted((a, b) => {
      if (b.visible === a.visible) {
        return 0;
      }
      return b.visible ? 1 : -1;
    });
  }, [workerTypesResponse, availableQualifications]);

  const [workerTypes, setWorkerTypes] = useState<Array<{ name: string; visible: boolean }>>([]);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (initialWorkerTypes.length > 0) {
      setWorkerTypes(initialWorkerTypes);
      setIsDirty(false);
    }
  }, [initialWorkerTypes]);

  // Shift count queries for active-shift guard
  const dateFilter = useMemo(() => {
    const effectiveTimezone = timezone || "America/Los_Angeles";
    const todayStart = startOfDayInTz(new Date(), { timeZone: effectiveTimezone });
    const twoWeeksFromNow = addDays(todayStart, 14);
    return {
      start: todayStart.toISOString(),
      end: twoWeeksFromNow.toISOString(),
    };
  }, [timezone]);

  const visibleTypeNames = useMemo(
    () => workerTypes.filter((wt) => wt.visible).map((wt) => wt.name),
    [workerTypes]
  );

  const shiftCountQueries = useQueries({
    queries: visibleTypeNames.map((typeName) => ({
      ...createFacilityShiftCountsQueryOptions({
        facilityId: workplaceId,
        dateFilter,
        timezone: timezone || "America/Los_Angeles",
        agentReq: typeName,
        enabled: Boolean(workplaceId) && !isWorkerTypesLoading,
      }),
    })),
  });

  const typesWithActiveShifts = useMemo(() => {
    const result = new Set<string>();
    for (const [index, typeName] of visibleTypeNames.entries()) {
      const data = shiftCountQueries[index]?.data;
      if (!data) {
        continue;
      }

      const hasShifts = Object.values(data).some(
        (dayCounts) => (dayCounts.openCount ?? 0) + (dayCounts.filledCount ?? 0) > 0
      );
      if (hasShifts) {
        result.add(typeName);
      }
    }
    return result;
  }, [visibleTypeNames, shiftCountQueries]);

  const isLoadingShiftCounts = shiftCountQueries.some((query) => query.isLoading);

  const workerTypesWithShiftData: WorkerTypeItem[] = useMemo(
    () =>
      workerTypes.map((wt) => ({
        ...wt,
        hasActiveShifts: typesWithActiveShifts.has(wt.name),
        isWithoutCharge: !availableQualifications.has(wt.name),
      })),
    [workerTypes, typesWithActiveShifts, availableQualifications]
  );

  const performedBy = session?.adminUserId ?? session?.user?._id ?? "";

  const handleVisibilityToggle = (index: number, checked: boolean) => {
    const item = workerTypes[index];
    if (!isDefined(item) || !availableQualifications.has(item.name) || item.visible === checked) {
      return;
    }
    setWorkerTypes((previous) => {
      const updated = [...previous];
      updated[index] = { ...item, visible: checked };
      return updated;
    });
    setIsDirty(true);
  };

  const handleMoveUp = (index: number) => {
    if (index <= 0) {
      return;
    }
    const item = workerTypes[index];
    if (!isDefined(item) || !availableQualifications.has(item.name)) {
      return;
    }
    setWorkerTypes((previous) => {
      const updated = [...previous];
      updated.splice(index, 1);
      updated.splice(index - 1, 0, item);
      return updated;
    });
    setIsDirty(true);
  };

  const handleMoveDown = (index: number) => {
    if (index >= workerTypes.length - 1) {
      return;
    }
    const item = workerTypes[index];
    if (!isDefined(item) || !availableQualifications.has(item.name)) {
      return;
    }
    setWorkerTypes((previous) => {
      const updated = [...previous];
      updated.splice(index, 1);
      updated.splice(index + 1, 0, item);
      return updated;
    });
    setIsDirty(true);
  };

  const handleSave = async () => {
    const visibleCount = workerTypes.filter((wt) => wt.visible).length;
    if (visibleCount === 0) {
      showErrorToast(tu("At least one professional type must be visible."));
      return;
    }

    try {
      await updateWorkerTypes({
        facilityId: workplaceId,
        performedBy,
        workerTypePreferences: {
          excluded: workerTypes.filter((wt) => !wt.visible).map((wt) => wt.name),
          sortOrder: workerTypes.map((wt) => wt.name),
        },
      });
      showSuccessToast(tu("Calendar settings saved successfully"));
      setIsDirty(false);
    } catch {
      showErrorToast(tu("Error saving calendar settings. Please try again later."));
    }
  };

  return (
    <CalendarDisplaySectionView
      isDirty={isDirty}
      isLoading={isWorkerTypesLoading}
      isLoadingShiftCounts={isLoadingShiftCounts}
      isUpdating={isUpdating}
      permissions={permissions}
      workerTypes={workerTypesWithShiftData}
      onMoveDown={(index: number) => {
        guardSettingsAction(() => {
          handleMoveDown(index);
        });
      }}
      onMoveUp={(index: number) => {
        guardSettingsAction(() => {
          handleMoveUp(index);
        });
      }}
      onSave={() => {
        guardSettingsAction(() => {
          void handleSave();
        });
      }}
      onVisibilityToggle={(index: number, checked: boolean) => {
        guardSettingsAction(() => {
          handleVisibilityToggle(index, checked);
        });
      }}
    />
  );
}
